import React from "react";
import { useTranslation } from "react-i18next";
import { FolderArrowDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "../../pages/Exams/TestBuilder/ui";
import { focusRing, useMediaQuery } from "../../pages/Exams/TestBuilder/ui";

/**
 * Bulk actions for the selected questions: how many are selected, a quiet "Clear" (an × before the count on phones,
 * after it from sm up) and the one main button, "Move to folder". It takes the place of the list's count line
 * (ResultsBar) at that line's height (44px on phones, 36px from sm up), so ticking a question moves nothing on the
 * screen: the blue bar is drawn a little taller than its box (::before), not with padding. It sticks to the top of
 * the scroll area while the list scrolls. The count never shrinks: on the narrowest phones the button's label takes
 * two lines instead. With `page` and `pageCount` (optional), from sm up it also says which page of the list is on
 * screen, as the selection is kept from page to page. `action` ({label, Icon, onClick, loading, disabled, title})
 * replaces "Move to folder" with another main button (e.g. My Questions' preview).
 */
export default function SelectionBar({ count, onMove, onClear, page, pageCount, action = null }) {
  const [t] = useTranslation();
  // Phones show "Clear" as an × alone: there its name is also its tooltip.
  const roomy = useMediaQuery("(min-width: 640px)");
  const clear = t("folders.move.clear");
  const paged = pageCount > 1;
  return (
    <div className="sticky top-1 z-[1] flex h-11 items-center gap-2 px-1 before:absolute before:inset-x-0 before:-inset-y-1 before:-z-10 before:rounded-2xl before:bg-blue-50 before:ring-1 before:ring-inset before:ring-blue-200 sm:top-2 sm:h-9 sm:gap-3 sm:pl-4 sm:pr-2 sm:before:-inset-y-2">
      {/* First in the reading order; on phones the × is drawn before it. */}
      <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-blue-900" aria-live="polite">
        {t("folders.move.selected", { count })}
      </span>
      <button
        type="button"
        onClick={onClear}
        title={roomy ? undefined : clear}
        className={`inline-flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-xl text-sm font-medium text-blue-900 transition duration-200 hover:bg-blue-100 max-sm:order-first max-sm:w-11 sm:h-9 sm:rounded-lg sm:px-3 ${focusRing}`}
      >
        <XMarkIcon className="h-5 w-5 sm:h-4 sm:w-4" aria-hidden="true" />
        <span className="max-sm:sr-only">{clear}</span>
      </button>
      {paged && (
        <span className="ml-auto whitespace-nowrap text-sm text-slate-600 max-sm:hidden">
          {t("question_list.page_of", { page, pages: pageCount })}
        </span>
      )}
      <button
        type="button"
        onClick={action ? action.onClick : onMove}
        disabled={!!action && (action.disabled || action.loading)}
        title={action?.title || undefined}
        className={`ml-auto inline-flex h-11 min-w-0 items-center justify-center gap-1.5 text-balance rounded-xl bg-blue-600 px-3.5 text-center text-sm font-medium leading-tight text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:h-9 sm:whitespace-nowrap sm:rounded-lg ${
          paged ? "sm:ml-0" : ""
        } ${focusRing}`}
      >
        {action?.loading ? (
          <Spinner className="h-4 w-4 shrink-0" />
        ) : (
          React.createElement(action?.Icon || FolderArrowDownIcon, { className: "h-4 w-4 shrink-0", "aria-hidden": "true" })
        )}
        {action ? action.label : t("folders.move.button")}
      </button>
    </div>
  );
}
